import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Newspaper, Mail, Zap, TrendingUp, CheckCircle2 } from 'lucide-react';

interface NewsletterModalProps {
  open: boolean;
  onClose: () => void;
}

export const NewsletterModal = ({ open, onClose }: NewsletterModalProps) => {
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState('');
  
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setEmail('');
      onClose();
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setEmail('');
      onClose();
    }, 2500);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md bg-card border-border text-foreground">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Mail className="w-6 h-6 text-primary" />
            <DialogTitle className="text-xl font-bold text-gradient">
              Stay in the Loop
            </DialogTitle>
          </div>
          <DialogDescription className="text-muted-foreground">
            Get the latest updates on chatMCP, MCP innovations, and exclusive insights delivered to your inbox.
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="flex flex-col items-center justify-center py-12 space-y-4 text-center">
            <div className="rounded-full bg-primary/20 p-4">
              <CheckCircle2 className="w-12 h-12 text-primary" />
            </div>
            <h3 className="text-lg font-bold">Subscribed!</h3>
            <p className="text-sm text-muted-foreground max-w-xs">
              Check your email for confirmation. You'll start receiving updates soon.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-muted/50 p-3 rounded-lg border border-border/50 flex flex-col items-center text-center space-y-1">
                <Zap className="w-5 h-5 text-primary mx-auto" />
                <p className="font-medium text-xs leading-tight">Latest Features</p>
              </div>
              <div className="bg-muted/50 p-3 rounded-lg border border-border/50 flex flex-col items-center text-center space-y-1">
                <TrendingUp className="w-5 h-5 text-primary mx-auto" />
                <p className="font-medium text-xs leading-tight">Best Practices</p>
              </div>
              <div className="bg-muted/50 p-3 rounded-lg border border-border/50 flex flex-col items-center text-center space-y-1">
                <Newspaper className="w-5 h-5 text-primary mx-auto" />
                <p className="font-medium text-xs leading-tight">MCP News</p>
              </div>
            </div>

            <form
              method="POST"
              action="#" 
              onSubmit={handleSubmit} 
              id="chatMCP_NewsletterForm"
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="newsletter-name">First Name (optional)</Label>
                <Input 
                  id="newsletter-name" 
                  name="name" 
                  placeholder="John" 
                  className="bg-background border-border focus:border-primary" 
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="newsletter-email">Email Address *</Label>
                <Input 
                  id="newsletter-email" 
                  name="email"
                  type="email" 
                  placeholder="you@company.com" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-background border-border focus:border-primary" 
                  required
                />
                <p className="text-xs text-muted-foreground">We respect your privacy. Unsubscribe anytime.</p>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-2 rounded-lg shadow-lg transition-all duration-200"
              >
                Subscribe
              </Button>
            </form>

            <p className="text-xs text-muted-foreground text-center leading-relaxed">
              By subscribing, you agree to receive marketing emails. We'll never share your email with third parties.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
